import { toast } from 'react-toastify'
import { axiosInstance } from '../helpers'
import { Dispatch, SetStateAction, useState } from 'react'
import { ServiceRequest, MaintenanceRequestForm } from '../types/serviceRequest'
import { IdentifiableObject } from '../types'

export const getBlankServiceRequestForm = (): MaintenanceRequestForm => ({
    title: "",
    description: "",
    priority: "medium",
    category: "",
    location: "",
    preferredTime: "",
    image: null,
});

export const useServiceRequest = () => {
    const [isLoading, setIsLoading] = useState(false)
    const [serviceRequestList, setServiceRequestList] = useState<
        ServiceRequest[]
    >([])

    const createServiceRequest = async (formData: FormData) => {
        setIsLoading(true)
        try {
            const response = await axiosInstance.post(
                'service-request/tenant_service_request/',
                formData,
            )

            const serviceRequest: ServiceRequest = response.data
            return serviceRequest
        } catch (e) {
            toast.error('Error submitting service request')
            setIsLoading(false)
            return Promise.reject(e)
        }
    }

    const getAIResults = async (serviceRequest: ServiceRequest) => {
        try {
            const response = await axiosInstance.post(
                `service-request/tenant_service_request/${serviceRequest.id}/get_ai_results/`,
            )

            toast.success('Service Request submitted successfully!')
            setIsLoading(false)
            return
        } catch (e) {
            toast.success('Service Request submitted successfully!')
            setIsLoading(false)
            return Promise.reject(e)
        }
    }

    const getServiceRequestList = async () => {
        setIsLoading(true)
        try {
            const res = await axiosInstance.get(
                'service-request/tenant_service_request/',
            )
            const serviceRequests: ServiceRequest[] = res.data
            setServiceRequestList(serviceRequests)
            setIsLoading(false)
            return serviceRequests
        } catch (e) {
            toast.error('Error getting service requests')
            setIsLoading(false)
            return Promise.reject(e)
        }
    }

    const getServiceRequestChannel = async (serviceRequest: ServiceRequest) => {
        try {
            const res = await axiosInstance.post(
                `service-request/tenant_service_request/${serviceRequest.id}/get_service_request_channel/`,
            )
            const channelId: number = res.data['id']

            const updatedServiceRequest: ServiceRequest = {
                ...serviceRequest,
                channel_id: channelId,
            }
            updateServiceRequestState(updatedServiceRequest)
            return channelId
        } catch (e) {
            return Promise.reject(e)
        }
    }

    const updateServiceRequestState = (serviceRequest: ServiceRequest) => {
        _insertOrUpdateIdentifiable(serviceRequest, setServiceRequestList)
    }

    const _insertOrUpdateIdentifiable = <T extends IdentifiableObject>(
        updatedValue: T,
        dispatch: Dispatch<SetStateAction<T[]>>,
    ) => {
        dispatch((old) => {
            const safeOldValue = old ? [...old] : []

            let found = false
            for (let i = 0; i < safeOldValue.length; i++) {
                const current = safeOldValue[i]
                if (current.id === updatedValue.id) {
                    safeOldValue[i] = updatedValue
                    found = true
                    break
                }
            }

            if (!found) {
                safeOldValue.push(updatedValue)
            }

            return safeOldValue
        })
    }

    return {
        isLoading,
        serviceRequestList,
        getServiceRequestList,
        createServiceRequest,
        getAIResults,
        getServiceRequestChannel,
        updateServiceRequestState,
    }
}
